import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Upload } from 'lucide-react';
import { authAPI } from '../services/api';
import LocationSelector from '@/components/LocationSelector';

const RequestAgency: React.FC = () => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        address: '',
        email: '',
        phone_number: '',
        iban: '',
    });

    // LocationSelector state
    const [countryId, setCountryId] = useState('IR');
    const [provinceId, setProvinceId] = useState('');
    const [cityId, setCityId] = useState('');

    const [avatar, setAvatar] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setAvatar(e.target.files[0]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const submitData = new FormData();

            // فیلدهای اصلی فرم
            Object.entries(formData).forEach(([key, value]) => {
                submitData.append(key, value);
            });

            // موقعیت مکانی (کلیدها مطابق بک‌اند شما)
            submitData.append('country_id', countryId);
            submitData.append('province_id', provinceId);
            submitData.append('city_id', cityId);

            // آواتار
            if (avatar) submitData.append('avatar', avatar);

            const response = await authAPI.requestAgency(submitData);
            setSuccess(response.message || 'درخواست نمایندگی با موفقیت ثبت شد!');

            // هدایت به صفحه ورود بعد از ۳ ثانیه
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: unknown) {
            const errorMessage =
                err instanceof Error && 'response' in err
                    ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                    : 'ثبت درخواست با خطا مواجه شد. دوباره تلاش کنید.';
            setError(errorMessage || 'ثبت درخواست با خطا مواجه شد. دوباره تلاش کنید.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-4" dir="rtl">
            <div className="max-w-2xl mx-auto py-8">
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-6 text-white shadow-xl mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold mb-1">درخواست نمایندگی املاک ۳</h1>
                    <p className="text-emerald-100">فرم زیر را تکمیل کنید تا تیم ما بررسی و با شما تماس بگیرد</p>
                </div>

                <Card className="border-green-200">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl font-bold text-center text-green-900">ثبت درخواست نمایندگی</CardTitle>
                        <CardDescription className="text-center text-green-700">
                            اطلاعات مورد نیاز برای بررسی درخواست شما
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {/* نام و نام خانوادگی */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name" className="text-green-900">نام</Label>
                                    <Input
                                        id="first_name"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        className="focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name" className="text-green-900">نام خانوادگی</Label>
                                    <Input
                                        id="last_name"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        className="focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>
                            </div>

                            {/* انتخاب کشور/استان/شهر */}
                            <div className="space-y-2">
                                <Label className="text-green-900">موقعیت مکانی</Label>
                                <LocationSelector
                                    selectedCountry={countryId}
                                    selectedProvince={provinceId}
                                    selectedCity={cityId}
                                    onCountryChange={setCountryId}
                                    onProvinceChange={setProvinceId}
                                    onCityChange={setCityId}
                                />
                            </div>

                            {/* آدرس کامل */}
                            <div className="space-y-2">
                                <Label htmlFor="address" className="text-green-900">آدرس</Label>
                                <Textarea
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    placeholder="آدرس کامل خود را وارد کنید"
                                    className="focus:ring-green-500 focus:border-green-500"
                                    required
                                />
                            </div>

                            {/* ایمیل و موبایل */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-green-900">ایمیل</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone_number" className="text-green-900">شماره موبایل</Label>
                                    <Input
                                        id="phone_number"
                                        name="phone_number"
                                        type="tel"
                                        dir="ltr"
                                        placeholder="09xxxxxxxxx"
                                        value={formData.phone_number}
                                        onChange={handleInputChange}
                                        className="focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>
                            </div>

                            {/* شبا */}
                            <div className="space-y-2">
                                <Label htmlFor="iban" className="text-green-900">شماره شبا</Label>
                                <Input
                                    id="iban"
                                    name="iban"
                                    dir="ltr"
                                    placeholder="IRxxxxxxxxxxxxxxxxxxxxxx"
                                    value={formData.iban}
                                    onChange={handleInputChange}
                                    className="focus:ring-green-500 focus:border-green-500"
                                    required
                                />
                            </div>

                            {/* آواتار */}
                            <div className="space-y-2">
                                <Label htmlFor="avatar" className="text-green-900">تصویر پروفایل (اختیاری)</Label>
                                <div className="flex items-center gap-2">
                                    <input
                                        id="avatar"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => document.getElementById('avatar')?.click()}
                                        className="w-full border-green-300 text-green-800 hover:bg-green-50"
                                    >
                                        <Upload className="ml-2 h-4 w-4" />
                                        {avatar ? avatar.name : 'انتخاب تصویر'}
                                    </Button>
                                </div>
                            </div>

                            {/* پیام‌ها */}
                            {error && (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}
                            {success && (
                                <Alert>
                                    <AlertDescription>{success}</AlertDescription>
                                </Alert>
                            )}

                            {/* ارسال */}
                            <Button
                                type="submit"
                                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                        در حال ارسال درخواست…
                                    </>
                                ) : (
                                    'ارسال درخواست'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <Link
                                to="/login"
                                className="flex items-center justify-center text-sm text-green-700 hover:text-green-900"
                            >
                                <ArrowLeft className="w-4 h-4 ml-1" />
                                بازگشت به ورود
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default RequestAgency;
